import styled from 'styled-components';
import { Show } from '@elfsight/lib-utils';
import {
  createReadableClass,
  formatAbbrNumber,
  getColorWithAlpha
} from '@elfsight/widget-utils';
import tinycolor from 'tinycolor2';
import { meta } from '../../../../constants';
import { useSocialFeedSettings } from '../../../utils';

interface HeaderCounterProps {
  count: number;
  text: string;
  isMobile?: boolean;
}

export function HeaderCounter({
  count = 0,
  text = '',
  isMobile
}: HeaderCounterProps) {
  const { alias } = meta;
  const { language, headerTextColor = '' } = useSocialFeedSettings();

  const isLinkedin = alias === 'linkedin-feed';

  const formattedText = isLinkedin ? text?.toLocaleLowerCase() : text;
  const formattedCount = isLinkedin
    ? new Intl.NumberFormat(language?.language ?? 'en').format(count)
    : formatAbbrNumber(count, language);

  const currentAlpha = tinycolor(headerTextColor).getAlpha();
  const finalAlpha = currentAlpha * 0.7;
  const colorWithAlpha = getColorWithAlpha(headerTextColor, finalAlpha);

  const ariaLabel = `${formattedCount} ${formattedText}`;

  return (
    <Container
      $isColumn={!isLinkedin && isMobile}
      role="group"
      aria-label={ariaLabel}
    >
      <Count
        $color={isLinkedin ? colorWithAlpha : headerTextColor}
        $fontSize={isLinkedin ? 13 : 14}
        aria-hidden="true"
      >
        {formattedCount}
      </Count>
      <Show when={!!formattedText}>
        <Text
          $color={colorWithAlpha}
          $fontSize={isLinkedin ? 13 : 12}
          aria-hidden="true"
        >
          {formattedText}
        </Text>
      </Show>
    </Container>
  );
}

const Container = styled.div.attrs({
  className: createReadableClass({
    component: 'header-counter',
    element: 'container'
  })
})<{
  $isColumn?: boolean;
}>`
  display: flex;
  flex-wrap: wrap;
  gap: 2px;
  align-items: center;
  justify-content: center;
  flex-direction: ${({ $isColumn }) => ($isColumn ? 'column' : 'row')};
`;

const Count = styled.div.attrs({
  className: createReadableClass({
    component: 'header-counter',
    element: 'count'
  })
})<{
  $fontSize: number;
  $color?: string;
}>`
  font-size: ${({ $fontSize }) => $fontSize}px;
  font-weight: 700;
  line-height: 18px;
  color: ${({ $color }) => $color};
`;

const Text = styled.div.attrs({
  className: createReadableClass({
    component: 'header-counter',
    element: 'text'
  })
})<{
  $fontSize: number;
  $color?: string;
}>`
  font-size: ${({ $fontSize }) => $fontSize}px;
  font-weight: 400;
  line-height: 14px;
  color: ${({ $color }) => $color};
`;
